/**
 * Human-readable cleanup for character-level diffs.
 *
 * A raw character LCS matches letters that just happen to line up, so "defines" -> "describes"
 * renders as interleaved noise: de<ins>scr</ins><del>f</del>i<ins>b</ins><del>n</del>es.
 * These passes regroup the edit script to whole words and word parts. They port
 * diff-match-patch's diff_cleanupSemantic + diff_cleanupMerge and run on code-point
 * arrays so multi-byte characters never split. Every pass keeps one rule:
 * delete+equal runs rebuild the old text and insert+equal the new, in order.
 */
import type { TSequenceOp, TSequenceOpType } from '../types';

export type TCharRun = { op: TSequenceOpType; chars: string[] };

const commonPrefixLength = (a: string[], b: string[]): number => {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i += 1;
  return i;
};

const commonSuffixLength = (a: string[], b: string[]): number => {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[a.length - 1 - i] === b[b.length - 1 - i]) i += 1;
  return i;
};

const charsEqual = (a: string[], b: string[]): boolean => a.length === b.length && a.every((c, i) => c === b[i]);

const toCharRuns = (charOps: readonly TSequenceOp<string>[]): TCharRun[] => {
  const runs: TCharRun[] = [];
  for (const op of charOps) {
    const char = op.type === 'delete' ? op.prevItem : op.currItem;
    const last = runs.at(-1);
    if (last && last.op === op.type) last.chars.push(char);
    else runs.push({ op: op.type, chars: [char] });
  }
  return runs;
};

/** An equal run no longer than the edits around it becomes delete+insert so those edits can merge. */
const removeTrivialEqualities = (runs: TCharRun[]): TCharRun[] => {
  const diffs = runs.map((r) => ({ op: r.op, chars: [...r.chars] }));
  const equalities: number[] = [];
  let lastEquality: string[] | null = null;
  let pointer = 0;
  let insertions1 = 0;
  let deletions1 = 0;
  let insertions2 = 0;
  let deletions2 = 0;

  while (pointer < diffs.length) {
    if (diffs[pointer].op === 'equal') {
      equalities.push(pointer);
      insertions1 = insertions2;
      deletions1 = deletions2;
      insertions2 = 0;
      deletions2 = 0;
      lastEquality = diffs[pointer].chars;
    } else {
      if (diffs[pointer].op === 'insert') insertions2 += diffs[pointer].chars.length;
      else deletions2 += diffs[pointer].chars.length;

      if (
        lastEquality &&
        lastEquality.length <= Math.max(insertions1, deletions1) &&
        lastEquality.length <= Math.max(insertions2, deletions2)
      ) {
        const index = equalities[equalities.length - 1];
        diffs.splice(index, 0, { op: 'delete', chars: [...lastEquality] });
        diffs[index + 1].op = 'insert';
        equalities.pop();
        equalities.pop();
        pointer = equalities.length > 0 ? equalities[equalities.length - 1] : -1;
        insertions1 = 0;
        deletions1 = 0;
        insertions2 = 0;
        deletions2 = 0;
        lastEquality = null;
      }
    }
    pointer += 1;
  }
  return diffs;
};

/** Within a run: move all deletes before all inserts, pull a shared start/end into equal, merge equal runs. */
const cleanupMergeOnce = (runs: TCharRun[]): TCharRun[] => {
  const out: TCharRun[] = [];
  let deleteBuffer: string[] = [];
  let insertBuffer: string[] = [];

  const appendEqual = (chars: string[]): void => {
    if (!chars.length) return;
    const last = out.at(-1);
    if (last && last.op === 'equal') last.chars.push(...chars);
    else out.push({ op: 'equal', chars: [...chars] });
  };

  const flush = (): string[] => {
    let suffix: string[] = [];
    if (deleteBuffer.length && insertBuffer.length) {
      const prefix = commonPrefixLength(insertBuffer, deleteBuffer);
      if (prefix) {
        appendEqual(insertBuffer.slice(0, prefix));
        insertBuffer = insertBuffer.slice(prefix);
        deleteBuffer = deleteBuffer.slice(prefix);
      }
      const suffixLength = commonSuffixLength(insertBuffer, deleteBuffer);
      if (suffixLength) {
        suffix = insertBuffer.slice(insertBuffer.length - suffixLength);
        insertBuffer = insertBuffer.slice(0, insertBuffer.length - suffixLength);
        deleteBuffer = deleteBuffer.slice(0, deleteBuffer.length - suffixLength);
      }
    }
    if (deleteBuffer.length) out.push({ op: 'delete', chars: deleteBuffer });
    if (insertBuffer.length) out.push({ op: 'insert', chars: insertBuffer });
    deleteBuffer = [];
    insertBuffer = [];
    return suffix;
  };

  for (const run of runs) {
    if (run.op === 'delete') deleteBuffer.push(...run.chars);
    else if (run.op === 'insert') insertBuffer.push(...run.chars);
    else appendEqual([...flush(), ...run.chars]);
  }
  appendEqual(flush());
  return out;
};

type TEditTriple = { before: TCharRun; edit: TCharRun; after: TCharRun };

const editBetweenEqualities = (runs: TCharRun[], pointer: number): TEditTriple | null => {
  const before = runs[pointer - 1];
  const edit = runs[pointer];
  const after = runs[pointer + 1];
  return before?.op === 'equal' && after?.op === 'equal' ? { before, edit, after } : null;
};

/** Slide a single edit sitting between two equal runs when the slide removes an equal run (asse<ins>tasse</ins>ssment -> <ins>asset</ins>assessment). */
const shiftEdits = (out: TCharRun[]): boolean => {
  let changed = false;
  let pointer = 1;
  while (pointer < out.length - 1) {
    const triple = editBetweenEqualities(out, pointer);
    if (triple) {
      const { before, edit, after } = triple;
      if (charsEqual(edit.chars.slice(edit.chars.length - before.chars.length), before.chars)) {
        edit.chars = [...before.chars, ...edit.chars.slice(0, edit.chars.length - before.chars.length)];
        after.chars = [...before.chars, ...after.chars];
        out.splice(pointer - 1, 1);
        changed = true;
      } else if (charsEqual(edit.chars.slice(0, after.chars.length), after.chars)) {
        before.chars = [...before.chars, ...after.chars];
        edit.chars = [...edit.chars.slice(after.chars.length), ...after.chars];
        out.splice(pointer + 1, 1);
        changed = true;
      }
    }
    pointer += 1;
  }
  return changed;
};

const cleanupMerge = (runs: TCharRun[], allowShift: boolean): TCharRun[] => {
  let merged = cleanupMergeOnce(runs);
  while (allowShift && shiftEdits(merged)) merged = cleanupMergeOnce(merged);
  return merged;
};

const isAlphaNumeric = (char: string): boolean => /[\p{L}\p{N}]/u.test(char);
const isWhitespace = (char: string): boolean => /\s/u.test(char);
const isLineBreak = (char: string): boolean => char === '\n' || char === '\r';

/** Higher score = a more natural place to split text (word, then space, then line edges). */
const boundaryScore = (left: string[], right: string[]): number => {
  if (!left.length || !right.length) return 6;
  const a = left[left.length - 1];
  const b = right[0];
  const nonWordA = !isAlphaNumeric(a);
  const nonWordB = !isAlphaNumeric(b);
  const spaceA = nonWordA && isWhitespace(a);
  const spaceB = nonWordB && isWhitespace(b);
  if ((spaceA && isLineBreak(a)) || (spaceB && isLineBreak(b))) return 4;
  if (nonWordA && !spaceA && spaceB) return 3;
  if (spaceA || spaceB) return 2;
  if (nonWordA || nonWordB) return 1;
  return 0;
};

const alignEditsToBoundaries = (diffs: TCharRun[]): TCharRun[] => {
  let pointer = 1;
  while (pointer < diffs.length - 1) {
    const triple = editBetweenEqualities(diffs, pointer);
    if (triple) {
      let equalityBefore = [...triple.before.chars];
      let edit = [...triple.edit.chars];
      let equalityAfter = [...triple.after.chars];

      // Shift the edit fully left, then step it right; `>=` keeps the rightmost placement on a score tie.
      const offset = commonSuffixLength(equalityBefore, edit);
      if (offset) {
        const common = edit.slice(edit.length - offset);
        equalityBefore = equalityBefore.slice(0, equalityBefore.length - offset);
        edit = [...common, ...edit.slice(0, edit.length - offset)];
        equalityAfter = [...common, ...equalityAfter];
      }

      let bestBefore = equalityBefore;
      let bestEdit = edit;
      let bestAfter = equalityAfter;
      let bestScore = boundaryScore(equalityBefore, edit) + boundaryScore(edit, equalityAfter);

      while (edit.length && equalityAfter.length && edit[0] === equalityAfter[0]) {
        equalityBefore = [...equalityBefore, edit[0]];
        edit = [...edit.slice(1), equalityAfter[0]];
        equalityAfter = equalityAfter.slice(1);
        const score = boundaryScore(equalityBefore, edit) + boundaryScore(edit, equalityAfter);
        if (score >= bestScore) {
          bestScore = score;
          bestBefore = equalityBefore;
          bestEdit = edit;
          bestAfter = equalityAfter;
        }
      }

      if (!charsEqual(triple.before.chars, bestBefore)) {
        if (bestBefore.length) {
          triple.before.chars = bestBefore;
        } else {
          diffs.splice(pointer - 1, 1);
          pointer -= 1;
        }
        triple.edit.chars = bestEdit;
        if (bestAfter.length) triple.after.chars = bestAfter;
        else diffs.splice(pointer + 1, 1);
      }
    }
    pointer += 1;
  }
  return diffs;
};

const commonOverlapLength = (a: string[], b: string[]): number => {
  let length = Math.min(a.length, b.length);
  while (length > 0) {
    if (charsEqual(a.slice(a.length - length), b.slice(0, length))) return length;
    length -= 1;
  }
  return 0;
};

/** Split a delete+insert that share an edge into delete, the shared run as equal, then insert. */
const extractOverlaps = (runs: TCharRun[]): TCharRun[] => {
  const out: TCharRun[] = [];
  for (let i = 0; i < runs.length; i += 1) {
    const current = runs[i];
    const next = runs[i + 1];
    if (current && next && current.op === 'delete' && next.op === 'insert') {
      const del = current.chars;
      const ins = next.chars;
      // Worth splitting only when the shared run covers at least half of one side.
      const isSubstantial = (overlap: number): boolean => overlap * 2 >= del.length || overlap * 2 >= ins.length;
      const deleteThenInsert = commonOverlapLength(del, ins);
      const insertThenDelete = commonOverlapLength(ins, del);
      if (deleteThenInsert >= insertThenDelete) {
        if (isSubstantial(deleteThenInsert)) {
          out.push({ op: 'delete', chars: del.slice(0, del.length - deleteThenInsert) });
          out.push({ op: 'equal', chars: ins.slice(0, deleteThenInsert) });
          out.push({ op: 'insert', chars: ins.slice(deleteThenInsert) });
          i += 1;
          continue;
        }
      } else if (isSubstantial(insertThenDelete)) {
        out.push({ op: 'insert', chars: ins.slice(0, ins.length - insertThenDelete) });
        out.push({ op: 'equal', chars: del.slice(0, insertThenDelete) });
        out.push({ op: 'delete', chars: del.slice(insertThenDelete) });
        i += 1;
        continue;
      }
    }
    out.push(current);
  }
  return out;
};

/**
 * Regroup a character-level LCS edit script into readable runs.
 *
 * Pass `allowShift: false` when the refined run mixes marks (e.g. bold on only some
 * characters). The edit-shifting passes re-pair which characters count as equal - safe
 * for plain text, but with marks tied to character position it can mislabel an unchanged
 * repeated character as changed.
 */
export const cleanupCharDiff = (charOps: readonly TSequenceOp<string>[], allowShift = true): TCharRun[] => {
  let runs = toCharRuns(charOps);
  runs = removeTrivialEqualities(runs);
  runs = cleanupMerge(runs, allowShift);
  if (allowShift) runs = alignEditsToBoundaries(runs);
  runs = extractOverlaps(runs);
  return cleanupMerge(runs, allowShift);
};
